import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { ProcessedError } from '../../utils/errorHandler';

export interface ToastItem {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title?: string;
  message: string;
  duration?: number;
  dismissible?: boolean;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface ToastProps {
  toast: ToastItem;
  onDismiss: (id: string) => void;
}

const Toast: React.FC<ToastProps> = ({ toast, onDismiss }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    // Show animation
    const showTimer = setTimeout(() => setIsVisible(true), 10);
    
    // Auto dismiss
    let dismissTimer: NodeJS.Timeout;
    if (toast.duration && toast.duration > 0) {
      dismissTimer = setTimeout(() => {
        handleDismiss();
      }, toast.duration);
    }

    return () => {
      clearTimeout(showTimer);
      if (dismissTimer) clearTimeout(dismissTimer);
    };
  }, [toast.duration]);

  const handleDismiss = useCallback(() => {
    if (!toast.dismissible) return;
    
    setIsLeaving(true);
    setTimeout(() => {
      onDismiss(toast.id);
    }, 150); // Animation duration
  }, [toast.id, toast.dismissible, onDismiss]);

  const getToastConfig = (type: ToastItem['type']) => {
    switch (type) {
      case 'success':
        return {
          containerClass: 'bg-green-50 border-green-200',
          iconClass: 'text-green-400',
          titleClass: 'text-green-800',
          messageClass: 'text-green-700',
          icon: CheckCircleIcon,
        };
      case 'error':
        return {
          containerClass: 'bg-red-50 border-red-200',
          iconClass: 'text-red-400',
          titleClass: 'text-red-800',
          messageClass: 'text-red-700',
          icon: XCircleIcon,
        };
      case 'warning':
        return {
          containerClass: 'bg-yellow-50 border-yellow-200',
          iconClass: 'text-yellow-400',
          titleClass: 'text-yellow-800',
          messageClass: 'text-yellow-700',
          icon: ExclamationTriangleIcon,
        };
      case 'info':
        return {
          containerClass: 'bg-blue-50 border-blue-200',
          iconClass: 'text-blue-400',
          titleClass: 'text-blue-800',
          messageClass: 'text-blue-700',
          icon: InformationCircleIcon,
        };
      default:
        return {
          containerClass: 'bg-gray-50 border-gray-200',
          iconClass: 'text-gray-400',
          titleClass: 'text-gray-800',
          messageClass: 'text-gray-700',
          icon: InformationCircleIcon,
        };
    }
  };

  const config = getToastConfig(toast.type);
  const IconComponent = config.icon;

  return (
    <div
      className={`
        max-w-sm w-full border rounded-lg p-4 shadow-lg backdrop-blur-sm
        transform transition-all duration-150 ease-in-out
        ${isVisible && !isLeaving ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
        ${config.containerClass}
      `}
    >
      <div className="flex">
        <div className="flex-shrink-0">
          <IconComponent className={`w-5 h-5 ${config.iconClass}`} />
        </div>
        <div className="ml-3 w-0 flex-1">
          {toast.title && (
            <p className={`text-sm font-medium ${config.titleClass}`}>
              {toast.title}
            </p>
          )}
          <p className={`${toast.title ? 'mt-1' : ''} text-sm ${config.messageClass}`}>
            {toast.message}
          </p>
          {toast.action && (
            <div className="mt-3">
              <button
                onClick={toast.action.onClick}
                className={`text-sm underline ${config.titleClass} hover:opacity-75`}
              >
                {toast.action.label}
              </button>
            </div>
          )}
        </div>
        {toast.dismissible && (
          <div className="ml-4 flex-shrink-0 flex">
            <button
              onClick={handleDismiss}
              className={`inline-flex ${config.iconClass} hover:opacity-75 focus:outline-none`}
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Toast Container Component
interface ToastContainerProps {
  toasts: ToastItem[];
  onDismiss: (id: string) => void;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
}

const ToastContainer: React.FC<ToastContainerProps> = ({ 
  toasts, 
  onDismiss, 
  position = 'top-right' 
}) => {
  const getPositionClasses = (position: ToastContainerProps['position']) => {
    switch (position) {
      case 'top-right':
        return 'top-4 right-4';
      case 'top-left':
        return 'top-4 left-4';
      case 'bottom-right':
        return 'bottom-4 right-4';
      case 'bottom-left':
        return 'bottom-4 left-4';
      case 'top-center':
        return 'top-4 left-1/2 transform -translate-x-1/2';
      case 'bottom-center':
        return 'bottom-4 left-1/2 transform -translate-x-1/2';
      default:
        return 'top-4 right-4';
    }
  };

  if (toasts.length === 0) return null;

  return (
    <div className={`fixed z-50 ${getPositionClasses(position)}`}>
      <div className="space-y-2">
        {toasts.map((toast) => (
          <Toast key={toast.id} toast={toast} onDismiss={onDismiss} />
        ))}
      </div>
    </div>
  );
};

// Toast Manager Hook
export const useToast = () => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const addToast = useCallback((toast: Omit<ToastItem, 'id'>) => {
    const id = Math.random().toString(36).substring(2, 9);
    const newToast: ToastItem = {
      id,
      duration: 5000,
      dismissible: true,
      ...toast,
    };

    setToasts((prev) => [...prev, newToast]);
    return id;
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  // Convenience methods
  const showSuccess = useCallback((message: string, options?: Partial<ToastItem>) => {
    return addToast({ type: 'success', message, ...options });
  }, [addToast]);

  const showError = useCallback((message: string, options?: Partial<ToastItem>) => {
    return addToast({ type: 'error', message, duration: 0, ...options });
  }, [addToast]);

  const showWarning = useCallback((message: string, options?: Partial<ToastItem>) => {
    return addToast({ type: 'warning', message, ...options });
  }, [addToast]);

  const showInfo = useCallback((message: string, options?: Partial<ToastItem>) => {
    return addToast({ type: 'info', message, ...options });
  }, [addToast]);

  // Show error from ProcessedError
  const showProcessedError = useCallback((error: ProcessedError) => {
    const options: Partial<ToastItem> = {
      title: error.title,
      duration: error.severity === 'critical' ? 0 : 7000,
    };

    if (error.canRetry && error.action === 'retry') {
      options.action = {
        label: 'Retry',
        onClick: () => {
          // This would need to be passed from the calling component
          console.log('Retry action clicked');
        },
      };
    }

    return addToast({ 
      type: 'error', 
      message: error.message, 
      ...options 
    });
  }, [addToast]);

  return {
    toasts,
    addToast,
    removeToast,
    clearAllToasts,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showProcessedError,
  };
};

// Toast Context for global toast management
interface ToastContextType {
  showSuccess: (message: string, options?: Partial<ToastItem>) => string;
  showError: (message: string, options?: Partial<ToastItem>) => string;
  showWarning: (message: string, options?: Partial<ToastItem>) => string;
  showInfo: (message: string, options?: Partial<ToastItem>) => string;
  showProcessedError: (error: ProcessedError) => string;
  removeToast: (id: string) => void;
  clearAllToasts: () => void;
}

const ToastContext = React.createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ 
  children: React.ReactNode;
  position?: ToastContainerProps['position'];
}> = ({ children, position = 'top-right' }) => {
  const toast = useToast();

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <ToastContainer 
        toasts={toast.toasts} 
        onDismiss={toast.removeToast}
        position={position}
      />
    </ToastContext.Provider>
  );
};

export const useToastContext = () => {
  const context = React.useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToastContext must be used within a ToastProvider');
  }
  return context;
};

export { ToastContainer };
export default Toast;